import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_NODE_URL,
  NEW_VENDORS_LIST,
  VENDORS_DELETE,
} from "../../../Constants/Config";
import { mapVendorApiItemToRow } from "./mapNewVendorListRow";

export const VENDOR_LIST_LIMIT = 25;

const initialState = {
  items: [],
  /** Full vendor_list pages from API — used for email duplicate checks on the No products tab. */
  allVendorItems: [],
  activeListType: "all",
  totalVendors: 0,
  totalNoProductVendors: 0,
  totalAssignedProducts: 0,
  hasMore: false,
  loading: false,
  loadingMore: false,
  deleteLoading: false,
  error: null,
};

const parseTotalCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

let activeVendorListRequests = 0;
let cancelVendorListSource;

const parseVendorListResponse = (response) => {
  const data = response?.data ?? {};
  const allRaw =
    data.vendor_list ??
    data.vendors ??
    data.data ??
    (Array.isArray(response?.data) ? response.data : []);
  const noProductRaw = data.vendors_with_no_product ?? [];
  const allList = Array.isArray(allRaw) ? allRaw : [];
  const noProductList = Array.isArray(noProductRaw) ? noProductRaw : [];
  const totalRaw =
    data.total_vendors ??
    data.total_vendor ??
    data.data?.total_vendors ??
    data.result?.total_vendors;
  const total_vendors = parseTotalCount(totalRaw);
  const noProductTotalRaw =
    data.total_vendors_with_no_product ??
    data.data?.total_vendors_with_no_product ??
    data.result?.total_vendors_with_no_product;
  const total_vendors_with_no_product = parseTotalCount(noProductTotalRaw);
  const assignedRaw =
    data.total_assigned_products ??
    data.total_assigned_product ??
    data.data?.total_assigned_products;
  const total_assigned_products = parseTotalCount(assignedRaw);
  return {
    allItems: allList.map(mapVendorApiItemToRow),
    noProductItems: noProductList.map(mapVendorApiItemToRow),
    total_vendors,
    total_vendors_with_no_product,
    total_assigned_products,
  };
};

export const fetchNewVendorsListData = createAsyncThunk(
  "newVendorList/fetchNewVendorsListData",
  async (
    {
      merchant_id,
      userTypeData,
      offset = 0,
      limit = VENDOR_LIST_LIMIT,
      search = null,
      listType = "all",
    },
    { rejectWithValue }
  ) => {
    try {
      if (cancelVendorListSource) {
        cancelVendorListSource.cancel("Previous vendor list request canceled");
      }
      cancelVendorListSource = axios.CancelToken.source();

      const trimmedSearch =
        typeof search === "string" && search.trim() ? search.trim() : null;

      const payload = {
        merchant_id,
        ...userTypeData,
        offset,
        limit,
        search: trimmedSearch,
      };
      const { token, ...newData } = payload;
      const response = await axios.post(BASE_NODE_URL + NEW_VENDORS_LIST, newData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        cancelToken: cancelVendorListSource.token,
      });

      const parsed = parseVendorListResponse(response);
      const items =
        listType === "empty" ? parsed.noProductItems : parsed.allItems;

      return {
        ...parsed,
        items,
        batchSize: items.length,
        offset,
        limit,
        listType,
      };
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ cancelled: true, offset, listType });
      }
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        error?.message ||
        "Could not load vendors.";
      return rejectWithValue({
        status,
        message,
        isNetwork: error?.message === "Network Error",
        offset,
        listType,
      });
    }
  }
);

export const deleteNewVendorsData = createAsyncThunk(
  "newVendorList/deleteNewVendorsData",
  async (
    { merchant_id, vendor_id, listVendorId, userTypeData },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        merchant_id,
        vendor_id,
        ...userTypeData,
      };
      const { token, ...newData } = payload;
      await axios.post(BASE_NODE_URL + VENDORS_DELETE, newData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      return { listVendorId };
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        error?.message ||
        "Could not delete vendor.";
      return rejectWithValue({
        status,
        message,
        isNetwork: error?.message === "Network Error",
      });
    }
  }
);

const computeVendorListHasMore = (
  loadedCount,
  totalVendors,
  batchSize,
  limit
) => {
  if (totalVendors > 0) {
    return loadedCount < totalVendors;
  }
  return batchSize >= limit;
};

const appendUniqueVendorItems = (existing, incoming) => {
  if (!incoming.length) return existing;
  const seen = new Set(existing.map((v) => String(v.id)));
  const unique = incoming.filter((v) => !seen.has(String(v.id)));
  return unique.length ? [...existing, ...unique] : existing;
};

const applyVendorListFulfilled = (state, action) => {
  activeVendorListRequests = Math.max(0, activeVendorListRequests - 1);
  const offset = action.meta.arg?.offset ?? 0;
  const listType = action.meta.arg?.listType ?? "all";
  const {
    items,
    allItems = [],
    total_vendors,
    total_vendors_with_no_product,
    total_assigned_products,
    batchSize,
    limit,
  } = action.payload;
  const pageSize = limit ?? action.meta.arg?.limit ?? VENDOR_LIST_LIMIT;
  const isStaleListResponse = listType !== state.activeListType;

  if (offset === 0 || total_vendors > 0) {
    state.totalVendors = total_vendors;
  }
  if (offset === 0 || total_vendors_with_no_product > 0) {
    state.totalNoProductVendors = total_vendors_with_no_product;
  }
  if (offset === 0) {
    state.totalAssignedProducts = total_assigned_products;
  }

  if (allItems.length) {
    if (offset === 0 && listType === "all") {
      state.allVendorItems = allItems;
    } else {
      state.allVendorItems = appendUniqueVendorItems(
        state.allVendorItems,
        allItems
      );
    }
  }

  if (!isStaleListResponse) {
    if (offset === 0) {
      state.items = items;
      state.loading = false;
    } else {
      state.items = appendUniqueVendorItems(state.items, items);
      state.loadingMore = false;
    }
    const totalForHasMore =
      listType === "empty" ? state.totalNoProductVendors : state.totalVendors;
    state.hasMore = computeVendorListHasMore(
      state.items.length,
      totalForHasMore > 0
        ? totalForHasMore
        : listType === "empty"
          ? total_vendors_with_no_product
          : total_vendors,
      batchSize,
      pageSize
    );
  } else if (offset === 0) {
    state.loading = activeVendorListRequests > 0;
  } else {
    state.loadingMore = activeVendorListRequests > 0;
  }

  state.error = null;
};

const applyVendorListPending = (state, action) => {
  const offset = action.meta.arg?.offset ?? 0;
  const listType = action.meta.arg?.listType ?? "all";
  state.error = null;
  activeVendorListRequests++;
  if (offset === 0) {
    state.activeListType = listType;
    state.loading = true;
    state.loadingMore = false;
    state.items = [];
    state.hasMore = false;
  } else {
    state.loadingMore = true;
  }
};

const applyVendorListRejected = (state, action) => {
  activeVendorListRequests = Math.max(0, activeVendorListRequests - 1);
  const offset = action.meta.arg?.offset ?? 0;
  if (offset === 0) {
    state.loading = activeVendorListRequests > 0;
  } else {
    state.loadingMore = activeVendorListRequests > 0;
  }
  if (action.payload?.cancelled) {
    return;
  }
  if (offset === 0) {
    state.loading = false;
  } else {
    state.loadingMore = false;
  }
  state.error = action.payload ?? null;
};

const newvendorListSlice = createSlice({
  name: "newVendorList",
  initialState,
  reducers: {
    clearNewVendorList: (state) => {
      state.items = [];
      state.allVendorItems = [];
      state.activeListType = "all";
      state.totalVendors = 0;
      state.totalNoProductVendors = 0;
      state.totalAssignedProducts = 0;
      state.hasMore = false;
      state.loading = false;
      state.loadingMore = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNewVendorsListData.pending, applyVendorListPending)
      .addCase(fetchNewVendorsListData.fulfilled, applyVendorListFulfilled)
      .addCase(fetchNewVendorsListData.rejected, applyVendorListRejected);

    builder
      .addCase(deleteNewVendorsData.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(deleteNewVendorsData.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const rm = action.payload.listVendorId;
        state.items = state.items.filter((v) => String(v.id) !== String(rm));
        if (state.totalVendors > 0) {
          state.totalVendors = Math.max(0, state.totalVendors - 1);
        }
        if (state.totalNoProductVendors > 0) {
          state.totalNoProductVendors = Math.max(
            0,
            state.totalNoProductVendors - 1
          );
        }
        const totalForHasMore =
          state.activeListType === "empty"
            ? state.totalNoProductVendors
            : state.totalVendors;
        state.hasMore = computeVendorListHasMore(
          state.items.length,
          totalForHasMore,
          0,
          VENDOR_LIST_LIMIT
        );
      })
      .addCase(deleteNewVendorsData.rejected, (state) => {
        state.deleteLoading = false;
      });
  },
});

export const { clearNewVendorList } = newvendorListSlice.actions;

export default newvendorListSlice.reducer;
